import moment from "moment";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { useRoadSearchStore } from "../../store/roadSearch/roadSearchStore";
import { useAuthStore } from "../../store/auth/authStore";
import { GET_AIRPORTS_API_URL } from "../../utils/constants";
import { getAPI } from "../../utils/api";

export type TokenPayload = {
  MerchantCode: string;
  MerchantHash: string;
};

/**
 * Sanitizes merchant code and hash parameters to prevent basic XSS or empty inputs.
 */
export const sanitizeCredentials = (code: string | null, hash: string | null): TokenPayload | null => {
  if (!code || !hash) return null;

  const sanitizedCode = code.replace(/[<>\"'&]/g, "").trim();
  const sanitizedHash = hash.replace(/[<>\"'&]/g, "").trim();

  if (!sanitizedCode || !sanitizedHash) return null;

  return { MerchantCode: sanitizedCode, MerchantHash: sanitizedHash };
};

const parsePayloadDate = (dateVal: any): any => {
  if (!dateVal) return undefined;
  if (typeof dateVal === "object") {
    const result: any = {};
    if (dateVal.from) {
      result.from = moment(dateVal.from);
    }
    if (dateVal.to) {
      result.to = moment(dateVal.to);
    }
    if (result.from || result.to) {
      return result;
    }
  }
  const m = moment(dateVal);
  return m.isValid() ? m : undefined;
};

/**
 * Processes base64 searchPayload, maps fields, updates appropriate flight/road stores, and navigates.
 */
export const processSearchPayload = async (
  searchPayload: string,
  navigate: (path: string) => void,
  setAirports?: (airports: any[], key: string) => void
): Promise<boolean> => {
  try {
    const decodedPayload = JSON.parse(atob(searchPayload));
    const { type, data } = decodedPayload;
 
    if (type === "flight") {
      const flightData: any = {
        ...data,
        DepartureDate: parsePayloadDate(data.DepartureDate),
        ReturnDate: parsePayloadDate(data.ReturnDate),
        multiCity: data.multiCity?.map((route: any) => ({
          ...route,
          DepartureDate: parsePayloadDate(route.DepartureDate),
        })),
      };

      try {
        let airportsList: any[] = [];
        const storedAirports = localStorage.getItem("airports");
        if (storedAirports) {
          airportsList = JSON.parse(storedAirports);
        } else {
          const airportsApi = await getAPI(GET_AIRPORTS_API_URL);
          if (airportsApi.status === 200 && Array.isArray(airportsApi.data)) {
            airportsList = airportsApi.data;
            localStorage.setItem("airports", JSON.stringify(airportsList));
            if (setAirports) {
              setAirports(airportsList, "airports");
            }
          }
        }
        if (Array.isArray(airportsList) && airportsList.length > 0) {
          const origin = airportsList.find((a: any) => a.Code === data.OriginLocationCode);
          const destination = airportsList.find((a: any) => a.Code === data.DestinationLocationCode);
          if (origin && destination) {
            flightData.isDomestic = origin.CountryCode === destination.CountryCode;
          }
        }
      } catch (err) {
        console.error("Failed to resolve trip type:", err);
      }

      useFlightSearchStore.getState().updateFormData(flightData);
      navigate("/search-listings");
      return true;
    } else if (type === "road") {
      const roadData = {
        ...data,
        DepartureDate: parsePayloadDate(data.DepartureDate),
        ReturnDate: parsePayloadDate(data.ReturnDate),
      };
      useRoadSearchStore.getState().updateFormData(roadData);
      navigate("/search-road-listings"); // Navigates to correct road listing route
      return true;
    }
  } catch (e) {
    console.error("Error parsing search payload:", e);
  }
  return false;
};

/**
 * Decrypts and processes the SSO user payload from query parameters.
 */
export const processSSOUser = (userParamRaw: string) => {
  try {
    const parsedDecryptedData = JSON.parse(atob(userParamRaw));
 
    const { AccessToken, User, RefreshToken, isAuthenticated } = parsedDecryptedData;

    if (User && (AccessToken || isAuthenticated)) {
      useAuthStore.getState().setB2cAuth(AccessToken || "", User, RefreshToken);
      useAuthStore.getState().setLastLoginMethod("email");
      useAuthStore.getState().login();
    }
  } catch (e) {
    console.error("Error parsing SSO user details:", e);
  }
};

 
export const resolveB2cHostUrl = (b2cHostUrlParam: string | null): string | null => {
  if (b2cHostUrlParam) return decodeURIComponent(b2cHostUrlParam);

  const referrer = document.referrer;
  if (!referrer) return null; // unknown host — do NOT assume the widget origin

  try {
    const refUrl = new URL(referrer);
    if (refUrl.origin === window.location.origin) return null; // referrer is the widget itself
    return refUrl.href; // Retains full URL (e.g. https://tripsportal.test.vggdev.com/merchant-wallet-transactions or https://tripstest.ng/ke)
  } catch (e) {
    console.error("Error parsing referrer URL:", e);
    return null;
  }
};